import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  Container,
  Group,
  Loader,
  MultiSelect,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import {
  LogoPaper,
  NpNotesPanel,
  NpScopesEditor,
  PageTitle,
  PartnerArchiveModal,
  PartnerBanModal,
  PartnerEngagementsPanel,
  PartnerIntegrationsPanel,
  PartnerLifecyclePanel,
  PartnerLocationFields,
  scopeFormValueToPayload,
  scopeToFormValue,
  validateScopes,
  type ScopeFieldErrors,
  type ScopeFormValue,
} from '@/components';
import {
  useBackofficePartnerStatus,
  useNetworkPartner,
  useNpEngagements,
  useSubjurisdictions,
} from '@/hooks/useNetworkPartners';
import {
  NP_LANGUAGES,
  type NetworkPartnerDetail,
  type NetworkPartnerPayload,
} from '@/types/network-partner';

const LANGUAGE_OPTIONS = NP_LANGUAGES.map((l) => ({
  value: l,
  label: l.charAt(0).toUpperCase() + l.slice(1),
}));

interface FormValues {
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  secondary_email: string;
  phone: string;
  mobile: string;
  title: string;
  company: string;
  location_country: string | null;
  location_subjurisdiction: string | null;
  location_city: string;
  languages: string[];
  bio: string;
  scopes: ScopeFormValue[];
}

const EMPTY_VALUES: FormValues = {
  first_name: '',
  last_name: '',
  name: '',
  email: '',
  secondary_email: '',
  phone: '',
  mobile: '',
  title: '',
  company: '',
  location_country: null,
  location_subjurisdiction: null,
  location_city: '',
  languages: [],
  bio: '',
  scopes: [],
};

function partnerToFormValues(partner: NetworkPartnerDetail): FormValues {
  return {
    first_name: partner.first_name ?? '',
    last_name: partner.last_name ?? '',
    name: partner.name ?? '',
    email: partner.email,
    secondary_email: partner.secondary_email ?? '',
    phone: partner.phone ?? '',
    mobile: partner.mobile ?? '',
    title: partner.title ?? '',
    company: partner.company ?? '',
    location_country: partner.location_country,
    location_subjurisdiction: partner.location_subjurisdiction,
    location_city: partner.location_city ?? '',
    languages: partner.languages,
    bio: partner.bio ?? '',
    scopes: partner.scopes.map(scopeToFormValue),
  };
}

function toPayload(values: FormValues): NetworkPartnerPayload {
  return {
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    name: values.name.trim() || null,
    email: values.email.trim(),
    secondary_email: values.secondary_email.trim() || null,
    phone: values.phone.trim() || null,
    mobile: values.mobile.trim() || null,
    title: values.title.trim() || null,
    company: values.company.trim() || null,
    location_country: values.location_country || null,
    location_subjurisdiction: values.location_subjurisdiction || null,
    location_city: values.location_city.trim() || null,
    languages: values.languages.map((l) => l.toLowerCase()),
    bio: values.bio.trim() || null,
    scopes: values.scopes.map(scopeFormValueToPayload),
  };
}

/** Form-level validation: the scope rules the API enforces (so Save is
 * blocked before a 422) plus the secondary email being a real second
 * address. Errors are keyed by Mantine form path. */
function validateForm(values: FormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [index, entry] of Object.entries(validateScopes(values.scopes))) {
    for (const [field, message] of Object.entries(entry)) {
      errors[`scopes.${index}.${field}`] = message;
    }
  }
  const secondary = values.secondary_email.trim().toLowerCase();
  if (secondary && secondary === values.email.trim().toLowerCase()) {
    errors.secondary_email = 'The secondary email must differ from the primary email';
  }
  return errors;
}

/** The form's path-keyed scope errors, regrouped per card. */
function scopeErrorsByCard(formErrors: Record<string, unknown>): Record<number, ScopeFieldErrors> {
  const out: Record<number, ScopeFieldErrors> = {};
  for (const [path, message] of Object.entries(formErrors)) {
    const match = /^scopes\.(\d+)\.(scope|max_price|currency)$/.exec(path);
    if (match && message) {
      const index = Number(match[1]);
      out[index] = { ...out[index], [match[2]]: String(message) };
    }
  }
  return out;
}

/** Engagement rows per page (the API caps a page at 200). */
const ENGAGEMENTS_PAGE_SIZE = 50;

export function NetworkPartnerEditPage() {
  const { npId } = useParams<{ npId: string }>();
  const isNew = !npId;
  const navigate = useNavigate();

  const { partner, isLoading, error, create, update, archive, restore, ban } = useNetworkPartner(
    npId ?? null
  );
  const backoffice = useBackofficePartnerStatus(npId ?? null);
  const [engagementsPage, setEngagementsPage] = useState(1);
  const engagements = useNpEngagements(npId ?? null, {
    page: engagementsPage,
    pageSize: ENGAGEMENTS_PAGE_SIZE,
  });
  const { items: subjurisdictions } = useSubjurisdictions();
  const [saving, setSaving] = useState(false);
  const [archiveOpen, archiveHandlers] = useDisclosure(false);
  const [banOpen, banHandlers] = useDisclosure(false);

  const form = useForm<FormValues>({ initialValues: EMPTY_VALUES, validate: validateForm });

  // Populate the form once the partner loads (or after save refresh).
  const partnerId = partner?.id;
  useEffect(() => {
    if (!partner) {
      return;
    }
    form.setValues(partnerToFormValues(partner));
    form.resetDirty();
    // `form` is intentionally omitted: Mantine's useForm returns a stable
    // handle and depending on it would re-populate on every keystroke.
  }, [partnerId]);

  const handleSubmit = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await create(toPayload(values));
        if (created) {
          navigate(`/admin/network-partners/${created.id}`, { replace: true });
        }
      } else {
        const updated = await update(toPayload(values));
        if (updated) {
          form.resetDirty();
        }
      }
    } finally {
      setSaving(false);
    }
  });

  // Banned partners are frozen: nothing on the profile is editable.
  const readOnly = partner?.status === 'banned';
  const scopeErrors = useMemo(() => scopeErrorsByCard(form.errors), [form.errors]);
  const field = (path: keyof FormValues) => ({
    ...form.getInputProps(path),
    disabled: readOnly,
  });

  if (!isNew && isLoading && !partner) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (!isNew && error && !partner) {
    return (
      <Container size="md" py="xl">
        <Text c="red">Could not load partner: {error.message}</Text>
      </Container>
    );
  }

  return (
    <Stack gap="xl">
      <PageTitle
        title={isNew ? 'New Network Partner' : (partner?.name ?? 'Network Partner')}
        subtitle={
          isNew
            ? 'New partners start as prospects — activate them once cleared for work'
            : 'Partner record — the source of truth for scopes, coverage, and lifecycle'
        }
      />

      <Container size="xl" px={0}>
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {partner && (
              <LogoPaper>
                <PartnerLifecyclePanel
                  partner={partner}
                  onSetStatus={(status) => void update({ status })}
                  onArchive={archiveHandlers.open}
                  onRestore={() => void restore()}
                  onBan={banHandlers.open}
                />
              </LogoPaper>
            )}

            <LogoPaper>
              <Stack gap="md">
                <Title order={4}>Identity & contact</Title>
                <Group grow>
                  <TextInput
                    label="First name"
                    required
                    {...field('first_name')}
                    data-testid="np-first-name"
                  />
                  <TextInput label="Last name" required {...field('last_name')} />
                </Group>
                <Group grow>
                  <TextInput
                    label="Display name"
                    placeholder="Defaults to first + last name"
                    {...field('name')}
                  />
                  <TextInput
                    label="Email"
                    required
                    type="email"
                    {...field('email')}
                    data-testid="np-email"
                  />
                  <TextInput
                    label="Secondary email"
                    description="Contact only — dispatch and chat use the primary email"
                    type="email"
                    {...field('secondary_email')}
                    data-testid="np-secondary-email"
                  />
                </Group>
                <Group grow>
                  <TextInput label="Phone" {...field('phone')} />
                  <TextInput label="Mobile" {...field('mobile')} />
                </Group>
                <Group grow>
                  <TextInput label="Title" {...field('title')} />
                  <TextInput label="Company" {...field('company')} />
                </Group>
                <PartnerLocationFields
                  value={{
                    country: form.values.location_country,
                    state: form.values.location_subjurisdiction,
                  }}
                  onChange={({ country, state }) => {
                    form.setFieldValue('location_country', country);
                    form.setFieldValue('location_subjurisdiction', state);
                  }}
                  subjurisdictions={subjurisdictions}
                  cityInputProps={field('location_city')}
                  readOnly={readOnly}
                />
                <MultiSelect
                  label="Languages"
                  placeholder="Pick languages"
                  data={LANGUAGE_OPTIONS}
                  searchable
                  clearable
                  {...field('languages')}
                  data-testid="np-languages"
                />
              </Stack>
            </LogoPaper>

            <LogoPaper>
              <Stack gap="md">
                <Stack gap={2}>
                  <Title order={4}>Scopes</Title>
                  <Text size="sm" c="dimmed">
                    What the partner can be engaged for — each scope carries its own price range,
                    typical turnaround, coverage, and capacity.
                  </Text>
                </Stack>
                <NpScopesEditor
                  value={form.values.scopes}
                  onChange={(next) => {
                    form.setFieldValue('scopes', next);
                    // Editing clears the path-keyed scope errors so the
                    // next submit re-validates from scratch.
                    form.clearErrors();
                  }}
                  errors={scopeErrors}
                  readOnly={readOnly}
                />
              </Stack>
            </LogoPaper>

            <LogoPaper>
              <Stack gap="md">
                <Title order={4}>Bio & notes</Title>
                <Textarea label="Bio" autosize minRows={3} {...field('bio')} />
                {partner ? (
                  <NpNotesPanel partnerId={partner.id} />
                ) : (
                  <Text size="sm" c="dimmed">
                    Notes can be added once the partner is created.
                  </Text>
                )}
              </Stack>
            </LogoPaper>

            {partner && (
              <LogoPaper>
                <PartnerIntegrationsPanel
                  status={backoffice.status}
                  isLoading={backoffice.isLoading}
                  isCreating={backoffice.isCreating}
                  readOnly={readOnly}
                  onCreateAccount={() => void backoffice.createAccount()}
                />
              </LogoPaper>
            )}

            {partner && (
              <LogoPaper>
                <PartnerEngagementsPanel
                  data={engagements.data}
                  page={engagementsPage}
                  pageSize={ENGAGEMENTS_PAGE_SIZE}
                  onPageChange={setEngagementsPage}
                />
              </LogoPaper>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={() => navigate('/admin/network-partners')}>
                Back to list
              </Button>
              {!readOnly && (
                <Button type="submit" loading={saving} data-testid="np-save">
                  {isNew ? 'Create partner' : 'Save changes'}
                </Button>
              )}
            </Group>
          </Stack>
        </form>
      </Container>

      <PartnerArchiveModal
        opened={archiveOpen}
        partnerName={partner?.name}
        onClose={archiveHandlers.close}
        onConfirm={() => void archive()}
      />
      <PartnerBanModal
        opened={banOpen}
        partnerName={partner?.name}
        onClose={banHandlers.close}
        onConfirm={async (reason) => (await ban(reason)) !== null}
      />
    </Stack>
  );
}
